import { HZ_VALUES } from "./audiogram";

/**
 * The "speech banana" — the banana-shaped region of an audiogram where the
 * phonemes of normal conversational speech fall. Thresholds plotted below it
 * (louder than it) are speech sounds the patient cannot hear unaided, which is
 * what makes it useful to show a patient in the chair.
 *
 * There is NO canonical outline: every audiometer vendor draws it slightly
 * differently. These coordinates are Suno's own, derived from the commonly
 * cited 250-8000 Hz / ~20-55 dB HL extent, shaped as the classic "smile" that
 * dips louder through the mid frequencies (vowel energy) and rises into the
 * quiet high frequencies (consonants).
 */

export type SpeechBananaOutlinePoint = {
  hz: number;
  /** Quiet edge, drawn along the top of the region. */
  top: number;
  /** Loud edge, drawn along the bottom of the region. */
  bottom: number;
};

export const SPEECH_BANANA_OUTLINE: SpeechBananaOutlinePoint[] = [
  { hz: 250, top: 22, bottom: 45 },
  { hz: 500, top: 25, bottom: 53 },
  { hz: 1000, top: 27, bottom: 55 },
  { hz: 1500, top: 27, bottom: 54 },
  { hz: 2000, top: 25, bottom: 50 },
  { hz: 3000, top: 22, bottom: 46 },
  { hz: 4000, top: 20, bottom: 42 },
  { hz: 6000, top: 18, bottom: 38 },
  { hz: 8000, top: 18, bottom: 36 }
];

export type SpeechBananaPhoneme = {
  hz: number;
  db: number;
  label: string;
};

/**
 * Individual speech sounds printed inside the region. Vowels and nasals sit low
 * and loud on the left; the quiet high-frequency consonants that carry
 * intelligibility (s, f, th) sit up and to the right.
 *
 * Every entry must land INSIDE the outline above at its frequency, and entries
 * sharing a frequency are kept >= 6 dB apart so the glyphs never collide (the
 * y axis renders roughly 3.4 px per dB at the chart's on-screen size).
 */
export const SPEECH_BANANA_PHONEMES: SpeechBananaPhoneme[] = [
  { hz: 250, db: 30, label: "m" },
  { hz: 250, db: 40, label: "u" },
  { hz: 500, db: 33, label: "n" },
  { hz: 500, db: 45, label: "o" },
  { hz: 750, db: 39, label: "ng" },
  { hz: 1000, db: 35, label: "e" },
  { hz: 1000, db: 48, label: "l" },
  { hz: 1500, db: 34, label: "i" },
  { hz: 1500, db: 47, label: "r" },
  { hz: 2000, db: 32, label: "g" },
  { hz: 2000, db: 44, label: "k" },
  { hz: 3000, db: 29, label: "ch" },
  { hz: 3000, db: 41, label: "sh" },
  { hz: 4000, db: 28, label: "f" },
  { hz: 4000, db: 38, label: "t" },
  { hz: 6000, db: 26, label: "s" },
  { hz: 6000, db: 35, label: "th" }
];

// Violet on purpose: it sits outside the red/blue ear-symbol convention AND
// outside the green-to-red hearing-loss severity ramp, so the banana can never
// be mistaken for either when both overlays are switched on.
export const SPEECH_BANANA_FILL_COLOR = "rgba(109, 76, 145, 0.2)";
export const SPEECH_BANANA_LINE_COLOR = "rgba(109, 76, 145, 0.85)";
export const SPEECH_BANANA_TEXT_COLOR = "rgba(78, 53, 107, 0.95)";
export const SPEECH_BANANA_LINE_WIDTH = 1.5;
export const SPEECH_BANANA_FONT = "bold 13px sans-serif";

/**
 * Position of an arbitrary frequency on the DEFAULT (category) x axis, which is
 * indexed by position in HZ_VALUES rather than by Hz. Frequencies that are not
 * themselves audiometric frequencies are interpolated in log2 space between
 * their neighbours, matching how the ear (and the logarithmic axis behind
 * AUDIOGRAM_LOGARITHMIC_AXIS) spaces them.
 *
 * Out-of-range frequencies clamp to the first/last index rather than
 * extrapolating off the plot.
 */
export const speechBananaFrequencyIndex = (hz: number): number => {
  const exactIndex = HZ_VALUES.indexOf(hz);
  if (exactIndex !== -1) return exactIndex;

  if (!Number.isFinite(hz) || hz <= HZ_VALUES[0]) return 0;
  const lastIndex = HZ_VALUES.length - 1;
  if (hz >= HZ_VALUES[lastIndex]) return lastIndex;

  for (let i = 0; i < lastIndex; i += 1) {
    if (hz > HZ_VALUES[i] && hz < HZ_VALUES[i + 1]) {
      const ratio =
        (Math.log2(hz) - Math.log2(HZ_VALUES[i])) /
        (Math.log2(HZ_VALUES[i + 1]) - Math.log2(HZ_VALUES[i]));
      return i + ratio;
    }
  }

  return lastIndex;
};

export type SpeechBananaPoint = { x: number; y: number };

export type SpeechBananaEdges = {
  /** Quiet edge, low frequency to high. */
  top: SpeechBananaPoint[];
  /** Loud edge, high frequency back to low — ready to trace as a return path. */
  bottom: SpeechBananaPoint[];
};

/**
 * The two edges the region is traced from, in draw order: across the top, down
 * the high-frequency end, back along the bottom, and closed up the
 * low-frequency end.
 *
 * The edges are kept SEPARATE rather than joined into one ring because the
 * curve through them must interpolate every anchor. Smoothing a single closed
 * ring pinches the shape to a point at 250 Hz and 8000 Hz — where the two
 * edges meet at a 180° turn — which drops the labels at those frequencies
 * outside the shaded region.
 *
 * Pixel mapping is injected so this stays pure and testable, and so the same
 * geometry serves both x-axis modes — the caller decides whether a frequency
 * resolves through `speechBananaFrequencyIndex` (category axis) or straight as
 * Hz (logarithmic axis).
 */
export const buildSpeechBananaEdges = (
  toX: (hz: number) => number,
  toY: (db: number) => number
): SpeechBananaEdges => ({
  top: SPEECH_BANANA_OUTLINE.map((point) => ({
    x: toX(point.hz),
    y: toY(point.top)
  })),
  bottom: [...SPEECH_BANANA_OUTLINE].reverse().map((point) => ({
    x: toX(point.hz),
    y: toY(point.bottom)
  }))
});
